import { useI18n } from 'i18n-utils'
import {
  doubleMSocketService,
  useContractChangeSymbol,
  useDoubleContractAssetsAccountStore,
  useDoubleContractKlineStore,
  useProjectTheme,
  useQuoteStore,
  useUserInfoStore,
} from 'store'
import { SocketEventName } from 'store/src/socket/SocketService'
import { useExchangeInfoStore } from 'store/src/stores/use-exchange-info'
import { UiUtils } from 'ui-components'
import { ContractCalculation } from 'utils'
import {
  ContractOrderType,
  ContractServices,
  OrderDirection,
  OrderPositionType,
  PendingOrderPreference,
  ReduceOnly,
  TriggerMode,
  TriggerType,
  type OrderBuyParams,
  ApiStatus,
} from 'network'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'

export enum ButtonType {
  /** 限价 */
  Limit = 'limit',
  /** 市价 */
  Market = 'market',
  /** 冰山委托 */
  Iceberg = 'iceberg',
  /** 限价止盈止损 */
  LimitTakeProfit = 'limitTakeProfit',
  /** 市价止盈止损 */
  MarketTakeProfit = 'marketTakeProfit',
}

export enum UserDoubleContractUnitType {
  /** 币 */
  Coin = 'coin',
  /** USDT */
  USDT = 'usdt',
}

type Tab = {
  name: string
  value: ButtonType
  children?: Tab[]
}

export const tabs: Tab[] = [
  {
    name: '限价',
    value: ButtonType.Limit,
  },
  {
    name: '市价',
    value: ButtonType.Market,
  },
  {
    name: '冰山委托',
    value: ButtonType.Iceberg,
  },
]

export const useContractOrdererStore = defineStore('contractOrderer', () => {
  const klineStore = useDoubleContractKlineStore()
  const doubleContractAssetsAccountStore = useDoubleContractAssetsAccountStore()
  const exchangeInfo = useExchangeInfoStore()
  const activeIndex = ref(1)
  const activeType = ref(ButtonType.Market)
  const limitPrice = ref('')
  const slider = ref(0)
  const userInfoStore = useUserInfoStore()
  const depth = ref<SocketResponse.HandicapResponse['data']>()
  const userDoubleContractUnitType = ref(window.localStorage.getItem(StorageIndexDBEnum.UserDoubleContractUnitType) || UserDoubleContractUnitType.USDT)
  const longGetAmountInputValue = ref('')
  const shortGetAmountInputValue = ref('')
  const { t } = useI18n()
  const isCheckedStopLossTakeProfit = ref(false)
  const quoteStore = useQuoteStore()

  const buyAmount = ref('')
  const isAmountFocus = ref(false)
  /** 开多保证金 */
  const longBailValue = ref('')
  /** 开空保证金 */
  const shortBailValue = ref('')

  /** 冰山委托-挂单笔数 */
  const iceCount = ref('')

  /** 冰山委托-选中触发价格复选框 */
  const isCheckedIceTriggerPrice = ref(false)
  /** 冰山委托-触发价格 */
  const iceTriggerPrice = ref('')
  /** 冰山委托-挂单偏好 */
  const pendingOrderPreference = ref(PendingOrderPreference.Fast)

  /** 可以开多仓的数量 */
  const longGetAmount = ref('')
  /** 可以开空单仓的数量 */
  const shortGetAmount = ref('')

  const longLoading = ref(false)
  const shortLoading = ref(false)

  const updateLeverageLoading = ref(false)
  const themeStore = useProjectTheme()
  useContractChangeSymbol((currentSymbolId) => {
    if (activeType.value === ButtonType.Limit) {
      limitPrice.value = `${quoteStore.allContractList.find((item) => item.symbolId === currentSymbolId)?.last}`
    }
  })

  const positions = computed(() => {
    return doubleContractAssetsAccountStore.allPositions
  })

  /** 止盈价格 */
  const takeProfitPrice = ref('')
  /** 止损价格 */
  const stopLossPrice = ref('')

  /** 是否是逐仓 */
  const isIsolated = computed(() => {
    if (positions.value.length === 0) return true
    const currentPosition = positions.value.find((item) => item.symbolId === klineStore.currentSymbolId)
    if (currentPosition) {
      return currentPosition.marginType === 1
    }
    return true
  })

  const currentQuote = computed(() => {
    if (!klineStore.currentSymbolId) return null
    return quoteStore.getSymbolBySymbolIdWithContractList(klineStore.currentSymbolId)
  })

  const availBalance = computed(() => {
    if (!userInfoStore.isLogin) return '--'
    const assetEntity = doubleContractAssetsAccountStore.assets
    const asset = assetEntity.find((item) => item.itemId === doubleContractAssetsAccountStore.usdtItemId)
    if (isIsolated.value) {
      return asset?.availableBalance || '0'
    }
    return asset?.crossAvailableBalance || '0'
  })

  const defaultLeverage = computed(() => {
    if (!klineStore.currentSymbolId) return 20
    const currentExchangeInfo = exchangeInfo.getContractSymbolInfoBySymbolId(klineStore.currentSymbolId)

    return currentExchangeInfo?.defaultLeverage || 20
  })

  const currentLeverage = computed(() => {
    const currentPosition = positions.value.find((item) => item.symbolId === klineStore.currentSymbolId)

    if (currentPosition) {
      return currentPosition.leverage
    }
    return defaultLeverage.value
  })

  const setActiveIndex = (index: number, type: ButtonType) => {
    activeIndex.value = index
    activeType.value = type
  }

  const getTabName = (index: number) => {
    if (tabs[index].children && tabs[index].children!.length > 0) {
      const children = tabs[index].children || []
      const tab = children.find((child) => child.value === activeType.value)
      return tab?.name || children[0].name
    }
    return tabs[index].name
  }

  const getMarks = (value: number) => {
    if (value === slider.value) {
      return `${value}%`
    }
    return ''
  }

  const sliderMarks = computed(() => {
    // 只有当slider的值等对应的百分比时显示marks，其他都显示空
    const marks = {
      0: getMarks(0),
      25: getMarks(25),
      50: getMarks(50),
      75: getMarks(75),
      100: getMarks(100),
    }
    return marks
  })

  const listenHandicapCallback = (response: SocketResponse.HandicapResponse) => {
    depth.value = response.data
    depth.value?.a.sort((a, b) => b[0].subtractStr(a[0]).toNumber())
    depth.value?.b.sort((a, b) => b[0].subtractStr(a[0]).toNumber())
    changeOpenPrice()
  }

  onMounted(() => {
    doubleMSocketService.onMessage<SocketResponse.HandicapResponse>(SocketEventName.Handicap, listenHandicapCallback)
  })

  const config = computed(() => {
    if (!klineStore.currentSymbolId) return null
    return exchangeInfo.getContractSymbolInfoBySymbolId(klineStore.currentSymbolId)
  })

  const getMarkPrice = (isLong: boolean) => {
    if (activeType.value === ButtonType.Limit) return limitPrice.value
    if (activeType.value === ButtonType.Iceberg) return currentQuote.value?.markPrice || '0'
    if (isLong) {
      if (!depth.value?.a.length) return currentQuote.value?.markPrice || '0'
      return Math.min(...(depth.value?.a?.map((item) => item[0].toNumber()) || []))
    }
    if (!depth.value?.b.length) return currentQuote.value?.markPrice || '0'
    return Math.max(...(depth.value?.b?.map((item) => item[0].toNumber()) || []))
  }

  const getCanOpenAmount = (isLong: boolean) => {
    const canOpenAmountFunc = isLong ? ContractCalculation.getOpenLongAmount : ContractCalculation.getOpenShortAmount
    const markPrice = getMarkPrice(isLong)

    const precision = userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin ? config.value?.quantityPrecision : 2

    return canOpenAmountFunc({
      price: markPrice,
      markPrice: currentQuote.value?.markPrice || '0',
      leverage: currentLeverage.value,
      balance: availBalance.value,
    })?.cutDecimal(precision).removeTrailingZero
  }

  const changeOpenPrice = () => {
    const percent = slider.value.divideStr(100)

    longGetAmount.value = getCanOpenAmount(true)
    shortGetAmount.value = getCanOpenAmount(false)

    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
      longGetAmountInputValue.value = longGetAmount.value
      shortGetAmountInputValue.value = shortGetAmount.value
    } else {
      const longMarkPrice = getMarkPrice(true)
      const shortMarkPrice = getMarkPrice(false)
      longGetAmountInputValue.value = longGetAmount.value.multiplyStr(longMarkPrice)
      shortGetAmountInputValue.value = shortGetAmount.value.multiplyStr(shortMarkPrice)
    }

    if (longGetAmountInputValue.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      longGetAmountInputValue.value = '--'
    }
    if (shortGetAmountInputValue.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      shortGetAmountInputValue.value = '--'
    }

    let tAmountLong
    let tAmountShort
    if (percent && percent.isGreaterThan(0)) {
      tAmountLong = longGetAmount.value.multiplyStr(percent)
      tAmountShort = shortGetAmount.value.multiplyStr(percent)
    } else {
      const longMarkPrice = getMarkPrice(true)
      const shortMarkPrice = getMarkPrice(false)
      const amount = buyAmount.value || '0'
      if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
        tAmountLong = amount
        tAmountShort = amount
      } else {
        tAmountLong = amount.divideStr(longMarkPrice)
        tAmountShort = amount.divideStr(shortMarkPrice)
      }
    }

    // 开多保证金
    if ((tAmountLong || 0).isLessThanOrEqualTo(0)) {
      longBailValue.value = '0'
    } else {
      const price = getMarkPrice(true)
      const value = ContractCalculation.trustMargin({
        qty: tAmountLong?.divideStr(config.value?.contractValue).cutDecimal(0),
        price: price,
        markPrice: currentQuote.value?.markPrice || '0',
        leverage: `${currentLeverage.value}`,
        direction: '1',
        contractValue: config.value?.contractValue,
      })
      longBailValue.value = value.formatNumberWithCommas() || '0'
    }

    // 开空保证金
    if ((tAmountShort || 0).isLessThanOrEqualTo(0)) {
      shortBailValue.value = '0'
    } else {
      const price = getMarkPrice(false)
      const value = ContractCalculation.trustMargin({
        qty: tAmountShort?.divideStr(config.value?.contractValue).cutDecimal(0),
        price,
        markPrice: currentQuote.value?.markPrice || '0',
        leverage: `${currentLeverage.value}`,
        direction: '-1',
        contractValue: config.value?.contractValue,
      })
      shortBailValue.value = value.formatNumberWithCommas() || '0'
    }
  }

  /** 赠金 */
  const bonusAssets = computed(() => {
    if (!userInfoStore.isLogin) return '--'
    return (doubleContractAssetsAccountStore.doubleContractAssetsAccount?.bonusAssets || '0').formatNumberWithCommas()
  })

  /** 冰山委托单笔数量 */
  const iceSingleAmount = computed(() => {
    let temp = '--'
    const value0 = buyAmount.value
    const value = iceCount.value
    if (
      (!value0.includes('%') && value0.toFloatNumber().isGreaterThanOrEqualTo(0)) ||
      value.isLessThanOrEqualTo(0) ||
      (value0.includes('%') && value0.replace('%', '').isLessThanOrEqualTo(0))
    ) {
      temp = '--'
    }
    let tempAmount = value0

    if (value0.includes('%') && value0.replace('%', '').toFloatNumber().isGreaterThan(0)) {
      const tempPercent = value0.replace('%', '').divideStr('100')
      tempAmount = availBalance.value.multiplyStr(tempPercent)
    }
    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
      if (value0.includes('%')) {
        temp = tempAmount.divideStr(value).divideStr(currentQuote.value?.markPrice).divideStr(config.value?.contractValue).cutDecimal(0).multiplyStr(config.value?.contractValue)
      } else {
        temp = tempAmount.divideStr(value).divideStr(config.value?.contractValue).cutDecimal(0).multiplyStr(config.value?.contractValue)
      }
    } else {
      temp = tempAmount.divideStr(value).cutDecimal(2)
    }
    const decimal = userDoubleContractUnitType.value === UserDoubleContractUnitType.USDT ? 2 : config.value?.quantityPrecision
    return temp.cutDecimal(decimal).formatCommonMoney(decimal)
  })

  watch(
    () => klineStore.currentKline?.close,
    () => {
      changeOpenPrice()
    },
  )

  watch(
    () => activeType.value,
    (type) => {
      if (type === ButtonType.Market) {
        limitPrice.value = ''
        changeOpenPrice()
      }
      if (type === ButtonType.Limit) {
        limitPrice.value = `${klineStore.currentKline?.close || ''}`
      }
    },
    {
      immediate: true,
    },
  )

  watch(
    () => availBalance.value,
    (value) => {
      if (value) {
        changeOpenPrice()
      }
    },
    {
      immediate: true,
    },
  )

  const onMarginSliderChange = (value: number) => {
    isAmountFocus.value = false
    buyAmount.value = `${value}`.cutDecimal(2).removeTrailingZero + '%'
    changeOpenPrice()
  }

  const onAmountFocus = () => {
    if (buyAmount.value.includes('%')) {
      isAmountFocus.value = true
      buyAmount.value = ''
      slider.value = 0
    }
  }
  const onAmountBlur = () => {
    isAmountFocus.value = false
  }

  const toggleAmountType = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.USDT) {
      userDoubleContractUnitType.value = UserDoubleContractUnitType.Coin
    } else {
      userDoubleContractUnitType.value = UserDoubleContractUnitType.USDT
    }

    if (activeType.value === ButtonType.Limit) {
      limitPrice.value = `${klineStore.currentKline?.close || ''}`
    }

    window.localStorage.setItem(StorageIndexDBEnum.UserDoubleContractUnitType, userDoubleContractUnitType.value)
    changeOpenPrice()
  }

  const currentOpenCoinName = computed(() => {
    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.USDT) {
      return 'USDT'
    }
    return klineStore.getFromSymbolName()
  })

  const settleName = computed(() => {
    return klineStore.getToSymbolName()
  })

  const onAmountInput = (value: string) => {
    slider.value = 0
    buyAmount.value = value
    changeOpenPrice()
  }

  const orderCheck = () => {
    if (activeType.value === ButtonType.Limit) {
      const price = limitPrice.value || '0'
      if (price.isEqualTo(0)) {
        throw UiUtils.showError(t('请输入价格'))
      }
    }
    if (buyAmount.value.length === 0) {
      throw UiUtils.showError(t('请输入数量'))
    }
    if (activeType.value === ButtonType.Iceberg) {
      if (iceCount.value.toFloatNumber().isLessThanOrEqualTo(0)) {
        throw UiUtils.showError(t('请输入挂单笔数'))
      }
      // 挂单笔数不能大于20
      if (iceCount.value.toFloatNumber().isGreaterThan(20)) {
        throw UiUtils.showError(t('挂单笔数不能大于20'))
      }
      if (isCheckedIceTriggerPrice.value) {
        if (iceTriggerPrice.value.toFloatNumber().isLessThanOrEqualTo(0)) {
          throw UiUtils.showError(t('请输入触发价格'))
        }
      }
    }

    if (availBalance.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      throw UiUtils.showError(t('资产不足'))
    }
  }

  const reset = () => {
    buyAmount.value = ''
    slider.value = 0
    limitPrice.value = ''
    isCheckedStopLossTakeProfit.value = false
    takeProfitPrice.value = ''
    stopLossPrice.value = ''
    isCheckedIceTriggerPrice.value = false
    iceCount.value = ''
    iceTriggerPrice.value = ''
    changeOpenPrice()
  }

  const submitOpenOrder = async (data: OrderBuyParams) => {
    if (data.direction === OrderDirection.Buy) {
      longLoading.value = true
    } else {
      shortLoading.value = true
    }
    if (isCheckedStopLossTakeProfit.value && stopLossPrice.value && takeProfitPrice.value) {
      const res = await ContractServices.buyDoubleContractWithTakeProfitOrStopLoss({
        ...data,
        lossTriggerPrice: stopLossPrice.value,
        profitTriggerPrice: takeProfitPrice.value,
      })
      if (res.status === ApiStatus.Success) {
        UiUtils.showSuccess(t('下单成功'))
        doubleContractAssetsAccountStore.refresh()
        reset()
      }
    } else {
      const res = await ContractServices.doubleContractOpenPosition(data)
      if (res.status === ApiStatus.Success) {
        UiUtils.showSuccess(t('下单成功'))
        doubleContractAssetsAccountStore.refresh()
        reset()
      }
    }
    if (data.direction === OrderDirection.Buy) {
      longLoading.value = false
    } else {
      shortLoading.value = false
    }
    return true
  }

  /**
   * 获取开仓数量
   * @param isLong 是否是开多
   * @returns 开仓数量
   */
  const getOpenAmount = (isLong: boolean) => {
    let amountLong = '0'
    let amountShort = '0'

    if (slider.value > 0) {
      amountLong = longGetAmount.value.multiplyStr(slider.value.divideStr(100))
      amountShort = shortGetAmount.value.multiplyStr(slider.value.divideStr(100))
    } else {
      if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
        amountLong = buyAmount.value
        amountShort = buyAmount.value
      } else {
        const tempBalance = buyAmount.value.divideStr(currentLeverage.value)
        const longMarkPrice = getOpenAmountMarkPrice(true)
        const shortMarkPrice = getOpenAmountMarkPrice(false)
        amountLong = tempBalance.multiplyStr(currentLeverage.value).divideStr(longMarkPrice)
        amountShort = tempBalance.multiplyStr(currentLeverage.value).divideStr(shortMarkPrice)
      }
    }
    // 新增 7 双向止盈止损市价 ; 下单类型 1:限价单；2:市价单；3:止损单；4:止盈单；5:市价止损单；6:市价止盈单 (当前仅使用限价)
    const number = isLong ? amountLong : amountShort
    return number
  }

  const submitIceOrder = async (data: { isLong: boolean; isClose: boolean }) => {
    const totalOrders = iceCount.value.toFloatNumber()
    const { isLong, isClose } = data
    const coinFirst = klineStore.getFromSymbolName()
    let number = '0'

    number = getOpenAmount(isLong)
    const temp = config.value?.contractValue.multiplyStr(currentQuote.value?.markPrice || '0') || '0'
    const singleQty = number.divideStr(totalOrders).divideStr(config.value?.contractValue).cutDecimal(0)
    if (singleQty.toFloatNumber().isLessThanOrEqualTo(0)) {
      throw UiUtils.showError(
        t('最小下单数量{value1} {name1}≈{value2} {name2}', {
          value1: config.value?.contractValue.removeTrailingZero,
          name1: coinFirst,
          value2: temp,
          name2: settleName.value,
        }),
      )
    }
    // 提交下单前的弹窗确认，需要判断用户是否已经勾选过不再提示，待做
    const triggerIce = isCheckedIceTriggerPrice.value && !!iceTriggerPrice.value

    const submit = async () => {
      if (isLong) {
        longLoading.value = true
      } else {
        shortLoading.value = true
      }
      const res = await ContractServices.icebergPlaceOrder({
        isSim: false,
        symbolId: `${klineStore.currentSymbolId}`,
        direction: isLong ? OrderDirection.Buy : OrderDirection.Sell,
        positionType: isLong ? OrderPositionType.Long : OrderPositionType.Short,
        singleQty: number.divideStr(totalOrders).divideStr(config.value?.contractValue).cutDecimal(0),
        orderNum: totalOrders,
        preferenceType: pendingOrderPreference.value,
        triggerMode: triggerIce ? TriggerMode.Price : TriggerMode.Immediate,
        triggerType: TriggerType.Mark,
        triggerPrice: triggerIce ? iceTriggerPrice.value : '0',
      })
      if (res.status === ApiStatus.Success) {
        UiUtils.showSuccess(t('下单成功'))
        doubleContractAssetsAccountStore.refresh()
        reset()
      }
      if (isLong) {
        longLoading.value = false
      } else {
        shortLoading.value = false
      }
    }

    UiUtils.showExchangeConfirm({
      title: klineStore.getSymbolName() + t('永续'),
      isIce: true,
      direction: {
        text: isClose ? (isLong ? t('平多') : t('平空')) : isLong ? t('做多') : t('做空'),
        color: isLong ? themeStore.theme.colors.up : themeStore.theme.colors.down,
        isTag: true,
      },
      leverage: `${isIsolated.value ? t('逐仓') : t('全仓')}${currentLeverage.value}X`,
      cellList: [
        {
          label: t('触发价格'),
          value: isCheckedIceTriggerPrice.value ? iceTriggerPrice.value : t('立即触发'),
        },
        {
          label: t('下单数量'),
          value: `${number.cutDecimal(config.value?.quantityPrecision).formatCommonMoney()} ${coinFirst}`,
        },
        {
          label: t('拆分单数'),
          value: totalOrders,
        },
        {
          label: t('单笔数量'),
          value: `${singleQty.multiplyStr(config.value?.contractValue).cutDecimal(config.value?.quantityPrecision).formatCommonMoney()} ${coinFirst}`,
        },
        {
          label: t('保证金'),
          value: `${isLong ? longBailValue.value : shortBailValue.value} USDT`,
        },
      ],
      onConfirm: async () => {
        await submit()
      },
    })
  }

  const getOpenAmountMarkPrice = (isLong: boolean) => {
    if (activeType.value === ButtonType.Iceberg) {
      return currentQuote.value?.markPrice || '0'
    }
    if (isLong) {
      if (!depth.value?.a.length) return currentQuote.value?.markPrice || '0'
      return Math.min(...(depth.value?.a?.map((item) => item[0].toNumber()) || []))
    }
    if (!depth.value?.b.length) return currentQuote.value?.markPrice || '0'
    return Math.max(...(depth.value?.b?.map((item) => item[0].toNumber()) || []))
  }

  const onPreOrderBuy = (data: { isLong: boolean }) => {
    if (!currentQuote.value?.markPrice) return

    const { isLong } = data
    const isMarket = activeType.value === ButtonType.Market
    const coinFirst = klineStore.getFromSymbolName()
    const number = getOpenAmount(isLong)
    if (number.divideStr(config.value?.contractValue).cutDecimal(0).toFloatNumber().isLessThanOrEqualTo(0)) {
      let temp = ''
      if (isMarket) {
        temp = config.value?.contractValue.multiplyStr(currentQuote.value?.markPrice || '0') || '0'
      } else {
        temp = config.value?.contractValue.multiplyStr(limitPrice.value) || '0'
      }
      throw UiUtils.showError(
        t('最小下单数量{value1} {name1}≈{value2} {name2}', {
          value1: config.value?.contractValue.removeTrailingZero,
          name1: coinFirst,
          value2: temp,
          name2: settleName.value,
        }),
      )
    }

    submitOpenOrder({
      isSim: false,
      symbolId: `${klineStore.currentSymbolId}`,
      qty: number.divideStr(config.value?.contractValue).cutDecimal(0),
      type: isMarket ? ContractOrderType.Market : ContractOrderType.Limit,
      price: isMarket ? '' : limitPrice.value,
      positionType: isLong ? OrderPositionType.Long : OrderPositionType.Short,
      direction: isLong ? OrderDirection.Buy : OrderDirection.Sell,
      reduceOnly: ReduceOnly.No,
    })
  }

  const onOrderBuyWithLong = () => {
    orderCheck()
    if (activeType.value === ButtonType.Iceberg) {
      submitIceOrder({ isLong: true, isClose: false })
      return
    }
    onPreOrderBuy({ isLong: true })
  }

  const onOrderBuyWithShort = () => {
    orderCheck()
    if (activeType.value === ButtonType.Iceberg) {
      submitIceOrder({ isLong: false, isClose: false })
      return
    }
    onPreOrderBuy({ isLong: false })
  }

  const setPendingOrderPreference = (index: number) => {
    if (index === 0) {
      pendingOrderPreference.value = PendingOrderPreference.Fast
    } else if (index === 1) {
      pendingOrderPreference.value = PendingOrderPreference.Better
    } else if (index === 2) {
      pendingOrderPreference.value = PendingOrderPreference.Queue
    }
  }

  const updateLeverage = async (leverage: number) => {
    updateLeverageLoading.value = true
    const res = await ContractServices.updateLeverage({
      symbolId: klineStore.currentSymbolId!,
      leverage: leverage,
    })
    if (res.status === ApiStatus.Success) {
      UiUtils.showSuccess(t('调整杠杆成功'))
      doubleContractAssetsAccountStore.refresh()
    }
    updateLeverageLoading.value = false
    return res.status === ApiStatus.Success
  }

  const currentSymbolId = computed(() => {
    return klineStore.currentSymbolId
  })

  return {
    isIsolated,
    currentLeverage,
    activeIndex,
    activeType,
    setActiveIndex,
    getTabName,
    limitPrice,
    slider,
    sliderMarks,
    availBalance,
    changeOpenPrice,
    buyAmount,
    onMarginSliderChange,
    isAmountFocus,
    onAmountFocus,
    onAmountBlur,
    longGetAmountInputValue,
    shortGetAmountInputValue,
    toggleAmountType,
    currentOpenCoinName,
    longBailValue,
    shortBailValue,
    bonusAssets,
    settleName,
    onAmountInput,
    onOrderBuyWithLong,
    isCheckedStopLossTakeProfit,
    takeProfitPrice,
    stopLossPrice,
    onOrderBuyWithShort,
    iceCount,
    iceTriggerPrice,
    isCheckedIceTriggerPrice,
    setPendingOrderPreference,
    longLoading,
    shortLoading,
    updateLeverage,
    updateLeverageLoading,
    currentSymbolId,
    userDoubleContractUnitType,
    iceSingleAmount,
  }
})
